import { Link } from 'react-router-dom';
import PageGlow from "@/components/PageGlow";
import { brand } from "@/config/brand";

const fg      = 'hsl(var(--foreground))';
const fgMuted = 'hsl(var(--muted-foreground))';
const surface = 'hsl(var(--card))';
const border  = 'hsl(var(--border))';
const GRAD    = '#5048E5';


interface Section { title: string; content: string[] }

const SECTIONS: Section[] = [
  {
    title: '1. About these terms',
    content: [
      `These Terms of Service govern your use of the ${brand.name} website at ${brand.domain}, the ${brand.name} mobile app available on the Google Play Store, and any services we provide to you. By accessing our website, downloading or using our app, or engaging our services, you agree to be bound by these terms.`,
      `${brand.name} is a digital consultancy based in the United Kingdom. References to "we", "us", or "our" refer to ${brand.name}. References to "you" or "your" refer to the individual or business accessing our website or app, or using our services.`,
      'We may update these terms from time to time. The date at the top of this page shows when they were last revised. Continued use of our website, app, or services after any changes constitutes acceptance of the updated terms.',
    ],
  },
  {
    title: '2. Use of our website and app',
    content: [
      'You may use our website and app for lawful purposes only. You must not use them in any way that breaches applicable laws or regulations, is fraudulent or harmful, or infringes the rights of any third party.',
      'You must not attempt to gain unauthorised access to any part of our website or app, the server on which they are hosted, or any server, computer, or database connected to them.',
      'You are responsible for maintaining the confidentiality of your account credentials. You must not share your login details with any other person, and you must notify us immediately if you suspect unauthorised access to your account.',
      'We reserve the right to suspend or terminate your access to our website or app at any time if we believe you have breached these terms.',
    ],
  },
  {
    title: '3. Account registration and security',
    content: [
      'To use certain features of the app (such as the client portal, messaging, and invoicing), you must create an account. You agree to provide accurate and complete information during registration and to keep it up to date.',
      'You are solely responsible for all activity that occurs under your account. We are not liable for any loss or damage arising from your failure to keep your account secure.',
      'The app offers optional biometric authentication (fingerprint or face recognition). If you enable this feature, your biometric data is handled entirely by your device\'s operating system and never transmitted to or stored by us. We cannot restore biometric access if you change or reset your device.',
      'We may require you to verify your identity before granting access to your account or processing certain requests.',
    ],
  },
  {
    title: '4. Our services',
    content: [
      'We provide digital consultancy services including web design and development, digital marketing, ongoing support, data analytics, privacy and GDPR compliance, and business setup services.',
      'The app provides a client portal where you can: view and pay invoices; view and sign service agreements; track project milestone progress; exchange messages and voice notes with your account manager; receive video and voice calls; and receive push notifications about activity on your account.',
      'The specific scope, deliverables, timeline, and fees for any engagement will be agreed in writing before work commences. These Terms of Service apply alongside any such agreement.',
      'We reserve the right to decline any project or engagement at our discretion, without being required to provide a reason.',
    ],
  },
  {
    title: '5. App communications and calls',
    content: [
      'The app enables real-time communication between you and your account manager, including text messaging, voice notes, video calls, and voice calls. These communications are intended for business purposes related to the services we provide.',
      'You agree not to use the in-app communication features to send unlawful, abusive, defamatory, harassing, or otherwise inappropriate content. We reserve the right to review messages where necessary to enforce these terms or comply with legal obligations.',
      'Calls made through the app are facilitated by a third-party provider (LiveKit). Calls may be recorded and summarised by AI for your convenience and our internal records, provided this is disclosed to you before or during the call. By participating in a recorded call, you consent to this recording and summarisation.',
      'We do not guarantee that video or voice calls will always be available, uninterrupted, or of a particular quality. Call quality depends on your device, network connection, and the third-party provider\'s infrastructure.',
    ],
  },
  {
    title: '6. Fees and payment',
    content: [
      'Our fees are agreed in advance and set out in a written proposal or contract. We will not carry out work beyond the agreed scope without your prior written approval and agreement on additional fees.',
      'Unless otherwise agreed, invoices are due within 14 days of the invoice date. Late payment may result in work being paused until the outstanding balance is settled. Overdue invoices may also incur statutory interest and compensation under the Late Payment of Commercial Debts (Interest) Act 1998.',
      'The app allows you to view invoices and make payments via our third-party payment processor (Stripe). By submitting a payment through the app, you agree to Stripe\'s terms of service and privacy policy. We do not store your payment card details.',
      'All fees are quoted exclusive of VAT unless otherwise stated. Where VAT applies, it will be added at the prevailing rate.',
    ],
  },

  {
    title: '7. Push notifications and device permissions',
    content: [
      'The app may send push notifications to your device to alert you about incoming calls, new messages, invoices, service agreements, and project updates. You can manage or disable notifications through your device settings or within the app.',
      'The app may request permissions to access your device\'s camera, microphone, and biometric sensors. These permissions are optional except where necessary for a specific feature you choose to use. You can revoke permissions at any time through your device settings, though this may limit app functionality.',
      'We do not access your device\'s contacts, photos, location, or other personal data beyond what is strictly necessary for the features you use.',
    ],
  },
  {
    title: '8. Intellectual property',
    content: [
      'Upon receipt of full payment for a project, all intellectual property rights in the deliverables created specifically for you (such as website code, design assets, and written content) transfer to you.',
      'We retain the right to use general skills, knowledge, and techniques acquired during the course of any engagement. We may also showcase completed work in our portfolio unless you request otherwise in writing.',
      'Any third-party materials incorporated into deliverables (such as stock images, fonts, or open-source software) remain subject to their respective licences. We will inform you of any such materials and their licence terms.',
    ],
  },
  {
    title: '9. Confidentiality',
    content: [
      'We treat all information you share with us as confidential. We will not disclose your confidential information to any third party without your consent, except where required by law.',
      'We ask that you treat any proprietary methods, pricing, or business information we share with you as confidential in return.',
      'Confidentiality obligations do not apply to information that is already publicly available, or that we are required to disclose by law or regulation.',
    ],
  },
  {
    title: '10. Limitation of liability',
    content: [
      'We provide our services with reasonable skill and care. However, we cannot guarantee that our services will always be uninterrupted, error-free, or that they will achieve any particular outcome for your business.',
      'The app is provided "as is" and "as available". We do not warrant that the app will always be available, secure, error-free, or compatible with every device or operating system. Updates to your device or our services may occasionally cause temporary disruption.',
      'To the fullest extent permitted by law, our total liability to you in connection with any engagement shall not exceed the total fees paid by you to us in the 12 months preceding the claim.',
      'We are not liable for any indirect, consequential, or special losses, including loss of profit, loss of business, or loss of data, even if we have been advised of the possibility of such losses.',
      'Nothing in these terms limits our liability for death or personal injury caused by our negligence, fraud or fraudulent misrepresentation, or any other liability that cannot be excluded by law.',
    ],
  },
  {
    title: '11. Third-party services',
    content: [
      'Our website and app rely on third-party services to function. These include Lovable Cloud (hosting, database, and authentication), Firebase Cloud Messaging (push notifications), LiveKit (video and voice calls), Stripe (payments), and Resend (email delivery).',
      'Your use of these third-party services is subject to their own terms and conditions. We are not responsible for the availability, performance, or terms of any third-party services, and we accept no liability for any loss or damage arising from your use of them.',
      'Where we set up third-party accounts on your behalf, we will do so in your name and transfer full control to you. You are responsible for maintaining the security of those accounts.',
    ],
  },
  {
    title: '12. Termination and account deletion',
    content: [
      'Either party may terminate an engagement by giving written notice as specified in the relevant contract or proposal. In the absence of a specific notice period, 14 days\' written notice is required.',
      `You may request deletion of your account and associated personal data at any time by emailing ${brand.contact.email} with the subject "Account deletion request". We will confirm receipt within 48 hours and complete deletion within 30 days, subject to any legal obligations we have to retain data.`,
      'Uninstalling the app from your device does not delete your account or data from our servers. You must request account deletion separately if you wish to remove your data entirely.',
      'Upon termination of an engagement, you will pay for all work completed up to the date of termination. We will provide you with all completed deliverables and, where applicable, work in progress.',
      'Clauses relating to intellectual property, confidentiality, and limitation of liability survive termination of any engagement.',
    ],
  },
  {
    title: '13. Governing law',
    content: [
      'These terms are governed by the laws of England and Wales. Any disputes arising from these terms or our services shall be subject to the exclusive jurisdiction of the courts of England and Wales.',
      'If any provision of these terms is found to be unenforceable, the remaining provisions will continue in full force and effect.',
      'These terms, together with any written proposal or contract, constitute the entire agreement between us in relation to the subject matter and supersede all prior agreements and understandings.',
    ],
  },
];

export default function TermsOfServicePage() {
  return (
    <>
      {/* Hero */}

      <section className="relative overflow-hidden py-10 md:py-14" style={{ background: 'hsl(var(--background))' }}>
        <PageGlow tall />
        <div className="relative mx-auto max-w-3xl px-6 text-center">
          <div>

            <h1 className="mb-4 text-4xl font-bold md:text-5xl"
              style={{ fontFamily: 'Outfit, sans-serif', color: fg, lineHeight: 1.06 }}>
              Terms of Service
            </h1>
            <p className="text-sm" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>
              Last updated: July 2026
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24" style={{ background: 'hsl(var(--background))' }}>
        <div className="mx-auto max-w-3xl px-6">
          {/* Intro card */}
          <div className="mb-8 rounded-2xl p-6"
            style={{ background: surface, border: `1px solid ${border}` }}>
            <p className="text-sm leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>
              Please read these terms carefully before using our website, downloading our app, or engaging our services. They set out the rules for how we work together, both online and through the Digitally Baffled mobile app. As with everything we do, we have written them in plain English — no unnecessary legalese. If you have any questions, just ask.
            </p>
          </div>

          {/* Sections */}
          <div className="flex flex-col gap-8">
            {SECTIONS.map(({ title, content }) => (
              <div key={title}>
                <h2 className="mb-4 text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>{title}</h2>
                <div className="flex flex-col gap-3">
                  {content.map((para, i) => (
                    <p key={i} className="text-sm leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>
                      {para}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Contact box */}
          <div className="mt-12 rounded-2xl p-8 text-center"
            style={{ background: GRAD }}>
            <h3 className="mb-2 text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: '#ffffff' }}>Questions about these terms?</h3>
            <p className="mb-1 text-sm" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'rgba(255,255,255,0.85)' }}>
              Email us at <a href={`mailto:${brand.contact.email}`} className="underline" style={{ color: '#ffffff' }}>{brand.contact.email}</a>
            </p>
            <p className="mb-4 text-sm" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'rgba(255,255,255,0.85)' }}>
              or call <a href={`tel:${brand.contact.phoneDisplay.replace(/\s/g, '')}`} className="underline" style={{ color: '#ffffff' }}>{brand.contact.phoneDisplay}</a> — we're {brand.contact.availability}.
            </p>
            <Link to="/privacy-policy"
              className="inline-flex items-center gap-1 text-sm underline"
              style={{ color: 'rgba(255,255,255,0.8)', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Read our Privacy Policy
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
